import React, { useCallback, useEffect, useState } from "react";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import DeleteIcon from "@mui/icons-material/Delete";
import IconButton from "@mui/material/IconButton";
import PartySearch from "../../../components/PartySearch";
import { api } from "../../../../api/apiClient";
import { Party } from "../../../types/types";
import ServiceInstanceSelector from "../../../components/ServiceSelection";
import { notify } from "../../../components-ui/toast";
import "./AudienceRuleBuilder.css";

interface Props {
  audienceId: number | null;
  onSaved: () => void;
  onCancel: () => void;
}

interface AudienceRule {
  audienceRuleId?: number;
  ruleType: string;
  serviceId?: number | null;
  serviceInstanceId?: number | null;
  lookbackDays?: number | null;
  comparisonOperator?: "AtLeast" | "AtMost" | "Exactly" | null;
  threshold?: number | null;
  isExclusion?: boolean;
  displayOrder?: number;
  _serviceName?: string | null;
  _sessionDateTime?: string | null;
}

interface AudienceMember extends Party {
  audienceMemberId?: number;
}

interface PreviewMember {
  partyId: number;
  displayName: string | null;
}

interface AudiencePreviewResponse {
  audienceId: number;
  totalCount: number;
  members: PreviewMember[];
}

const createDefaultRule = (): AudienceRule => ({
  ruleType: "AttendedService",
  serviceId: null,
  serviceInstanceId: null,
  lookbackDays: null,
  isExclusion: false,
});

const automaticSelectionOptions = [
  { value: "AttendedService", label: "Attended a service" },
  { value: "AttendedSession", label: "Attended a specific session" },
  { value: "NotAttendedRecently", label: "Have not attended recently" },
  { value: "NeverAttendedService", label: "Have never attended a service" },
  { value: "AttendanceCount", label: "Match an attendance count" },
  { value: "ServiceMember", label: "Are members of a service" },
];

export default function UpsertAudienceTab({
  audienceId,
  onSaved,
  onCancel,
}: Props) {
  const [name, setName] = useState("");
  const [members, setMembers] = useState<AudienceMember[]>([]);
  const [rules, setRules] = useState<AudienceRule[]>(() =>
    audienceId ? [] : [createDefaultRule()],
  );

  // 🔥 NEW STATE
  const [previewData, setPreviewData] =
    useState<AudiencePreviewResponse | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);

  const loadAudience = useCallback(async () => {
    const data = await api<any>(`/api/audience/${audienceId}`);

    setName(data.name);
    setMembers(data.members || []);
    setRules(data.rules || []);
  }, [audienceId]);

  useEffect(() => {
    if (audienceId) loadAudience();
  }, [audienceId, loadAudience]);

  // PREVIEW HANDLER
  const handlePreview = async () => {
    if (!audienceId) {
      notify.error("Please save the audience before previewing");
      return;
    }

    setLoadingPreview(true);

    try {
      const data = await api<AudiencePreviewResponse>(
        `/api/audience/${audienceId}/preview`,
      );
      setPreviewData(data);
    } catch {
      notify.error("Failed to load preview");
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleSubmit = async () => {
    if (!name.trim()) {
      notify.error("Enter an audience name");
      return;
    }

    if (rules.length > 1) {
      notify.error("Keep one automatic selection rule before saving");
      return;
    }

    if (rules.some((rule) => rule.isExclusion)) {
      notify.error(
        "Phase 1 supports inclusion rules only. Remove the exclusion rule before saving",
      );
      return;
    }

    const invalidRule = rules.find(
      (rule) =>
        !rule.serviceId ||
        (rule.ruleType === "AttendedSession" && !rule.serviceInstanceId) ||
        (["NotAttendedRecently", "AttendanceCount"].includes(rule.ruleType) &&
          (!rule.lookbackDays || rule.lookbackDays <= 0)) ||
        (rule.ruleType === "AttendanceCount" &&
          (!rule.threshold || rule.threshold <= 0)),
    );
    if (invalidRule) {
      notify.error("Complete all required audience rule fields");
      return;
    }

    const payload = {
      audienceId: audienceId ?? 0,
      name,
      ruleMatchType: "Any",
      members: members.map((m) => ({
        audienceMemberId: m.audienceMemberId ?? 0,
        partyId: m.partyId,
      })),
      rules: rules.map((rule, index) => ({
        audienceRuleId: rule.audienceRuleId ?? 0,
        ruleType: rule.ruleType,
        serviceId: rule.serviceId ?? null,
        serviceInstanceId: rule.serviceInstanceId ?? null,
        lookbackDays: rule.lookbackDays ?? null,
        comparisonOperator: rule.comparisonOperator ?? null,
        threshold: rule.threshold ?? null,
        isExclusion: false,
        displayOrder: index,
      })),
    };

    if (audienceId) {
      await api(`/api/audience/${audienceId}`, {
        method: "PUT",
        body: payload,
        notifySuccess: "Audience updated successfully",
      });
    } else {
      await api("/api/audience", {
        method: "POST",
        body: payload,
        notifySuccess: "Audience created successfully",
      });
    }

    onSaved();
  };

  const updateRule = (index: number, changes: Partial<AudienceRule>) => {
    setRules((current) =>
      current.map((item, itemIndex) =>
        itemIndex === index ? { ...item, ...changes } : item,
      ),
    );
  };

  return (
    <>
      {/* DETAILS */}
      <div className="card">
        <div className="card-title">Contact List Details</div>

        <div className="form-group">
          <label className="form-label">Name</label>
          <input
            className="form-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>
      </div>

      {/* RULE */}
      <div className="card">
        <div className="card-title">Automatic Selection</div>

        {rules.length === 0 && (
          <div className="form-group audience-rule-type-selector">
            <select
              id="automatic-selection-type"
              className="form-input"
              aria-label="Choose a rule for automatic selection"
              value=""
              onChange={(event) => {
                if (!event.target.value) return;
                setRules([
                  {
                    ...createDefaultRule(),
                    ruleType: event.target.value,
                  },
                ]);
              }}
            >
              <option value="">Choose a rule for automatic selection</option>
              {automaticSelectionOptions.map((option) => (
                <option value={option.value} key={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        )}

        {rules.length > 1 && (
          <div className="notification notification--warning audience-rule-legacy-warning">
            This contact list has multiple existing rules. Choose one to keep
            before saving.
          </div>
        )}

        {rules.some((rule) => rule.isExclusion) && (
          <div className="notification notification--warning audience-rule-legacy-warning">
            This contact list has an exclusion rule. Phase 1 supports inclusion
            rules only; remove the exclusion rule before saving.
          </div>
        )}

        <div className="audience-rule-list">
          {rules.map((rule, index) => {
            const showsLookback = [
              "AttendedService",
              "NotAttendedRecently",
              "AttendanceCount",
            ].includes(rule.ruleType);
            const requiresLookback = [
              "NotAttendedRecently",
              "AttendanceCount",
            ].includes(rule.ruleType);

            return (
              <div
                className="audience-rule-card"
                key={rule.audienceRuleId ?? `new-${index}`}
              >
                {(rules.length > 1 || rule.isExclusion) && (
                  <div className="card-header-row audience-rule-card-header">
                    <strong>Existing selection {index + 1}</strong>
                    <button
                      type="button"
                      className="link-button link-button-with-icon"
                      title="Remove automatic selection"
                      aria-label="Remove automatic selection"
                      onClick={() =>
                        setRules((current) =>
                          current.filter((_, itemIndex) => itemIndex !== index),
                        )
                      }
                    >
                      <DeleteIcon fontSize="small" />
                      <span>Remove</span>
                    </button>
                  </div>
                )}

                <div className="form-group">
                  <select
                    className="form-input"
                    aria-label="Automatic selection rule"
                    value={rule.ruleType}
                    onChange={(event) => {
                      if (!event.target.value) {
                        setRules((current) =>
                          current.filter((_, itemIndex) => itemIndex !== index),
                        );
                        return;
                      }

                      updateRule(index, {
                        ruleType: event.target.value,
                        serviceInstanceId: null,
                        lookbackDays: null,
                        comparisonOperator: null,
                        threshold: null,
                      });
                    }}
                  >
                    <option value="">Remove automatic selection</option>
                    {automaticSelectionOptions.map((option) => (
                      <option value={option.value} key={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                <ServiceInstanceSelector
                  value={{
                    serviceId: rule.serviceId ?? null,
                    serviceInstanceId: rule.serviceInstanceId ?? null,
                  }}
                  showSession={rule.ruleType === "AttendedSession"}
                  onChange={(val) =>
                    updateRule(index, {
                      serviceId: val.serviceId,
                      serviceInstanceId: val.serviceInstanceId,
                      _serviceName: val.serviceName,
                      _sessionDateTime: val.sessionDateTime,
                    })
                  }
                />

                {showsLookback && (
                  <div className="form-group">
                    <label className="form-label">
                      Look back (days){requiresLookback ? " *" : ""}
                    </label>
                    <input
                      type="number"
                      min={1}
                      className="form-input"
                      value={rule.lookbackDays ?? ""}
                      placeholder={requiresLookback ? "Required" : "Any time"}
                      onChange={(event) =>
                        updateRule(index, {
                          lookbackDays: event.target.value
                            ? Number(event.target.value)
                            : null,
                        })
                      }
                    />
                  </div>
                )}

                {rule.ruleType === "AttendanceCount" && (
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Comparison</label>
                      <select
                        className="form-input"
                        value={rule.comparisonOperator ?? "AtLeast"}
                        onChange={(event) =>
                          updateRule(index, {
                            comparisonOperator: event.target
                              .value as AudienceRule["comparisonOperator"],
                          })
                        }
                      >
                        <option value="AtLeast">At least</option>
                        <option value="AtMost">At most</option>
                        <option value="Exactly">Exactly</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Sessions</label>
                      <input
                        type="number"
                        min={1}
                        className="form-input"
                        value={rule.threshold ?? ""}
                        onChange={(event) =>
                          updateRule(index, {
                            threshold: event.target.value
                              ? Number(event.target.value)
                              : null,
                            comparisonOperator:
                              rule.comparisonOperator ?? "AtLeast",
                          })
                        }
                      />
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* MEMBERS */}
      <div className="card">
        <div className="card-title">Add Contacts Manually</div>

        <p className="field-hint audience-manual-intro">
          Search for specific contacts to add manually.
          {/* {rules.length > 0
            ? "Search for specific contacts to add. They will be included even if they do not match the automatic selection above."
            : "Search for contacts to build this list manually."} */}
        </p>

        <PartySearch
          onPartySelected={(party) => {
            if (!party) return;

            setMembers((prev) =>
              prev.some((p) => p.partyId === party.partyId)
                ? prev
                : [...prev, party],
            );
          }}
        />

        {/* MEMBERS GRID */}
        <div className="audience-manual-count">
          {members.length} manually added{" "}
          {members.length === 1 ? "contact" : "contacts"}
        </div>
        <div style={{ height: 300, marginTop: "0.5rem" }}>
          <DataGrid
            density="compact"
            rows={members.map((m) => ({ id: m.partyId, ...m }))}
            columns={
              [
                { field: "displayName", headerName: "Name", flex: 1 },
                {
                  field: "actions",
                  headerName: "",
                  width: 60,
                  renderCell: (params) => (
                    <IconButton
                      size="small"
                      title="Delete audience member"
                      onClick={() =>
                        setMembers((prev) =>
                          prev.filter((x) => x.partyId !== params.row.partyId),
                        )
                      }
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  ),
                },
              ] as GridColDef[]
            }
            pageSizeOptions={[5, 10]}
            disableRowSelectionOnClick
          />
        </div>

        {/* PREVIEW BUTTON */}
        <div style={{ marginTop: "0.5rem" }}>
          <button
            className="form-submit-button"
            onClick={handlePreview}
            disabled={!audienceId}
          >
            Preview
          </button>
        </div>

        {/* 🔥 PREVIEW GRID */}
        {previewData && (
          <div style={{ height: 350, marginTop: "1rem" }}>
            <div className="card-title">Preview ({previewData.totalCount})</div>

            <DataGrid
              density="compact"
              loading={loadingPreview}
              rows={previewData.members.map((m) => ({
                id: m.partyId,
                ...m,
              }))}
              columns={
                [
                  {
                    field: "displayName",
                    headerName: "Name",
                    flex: 1,
                  },
                  {
                    field: "partyId",
                    headerName: "Party ID",
                    width: 140,
                  },
                ] as GridColDef[]
              }
              pageSizeOptions={[5, 10, 25]}
              disableRowSelectionOnClick
            />
          </div>
        )}
      </div>

      {/* ACTIONS */}
      <div className="form-actions">
        <button className="form-submit-button" onClick={handleSubmit}>
          Save
        </button>
        <button className="form-submit-button" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </>
  );
}
